import React, { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { SanityImage } from '@/types'
import OptimizedImage from '@/components/ui/OptimizedImage'

interface ProjectGalleryProps {
  images: SanityImage[]
  projectTitle: string
}

const ProjectGallery: React.FC<ProjectGalleryProps> = ({ images, projectTitle }) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  const nextImage = useCallback(() => {
    setIsLoading(true)
    setActiveImageIndex(prev => (prev + 1) % images.length)
  }, [images.length])

  const prevImage = useCallback(() => {
    setIsLoading(true)
    setActiveImageIndex(prev => (prev - 1 + images.length) % images.length)
  }, [images.length])

  const handleImageLoad = useCallback(() => {
    setIsLoading(false)
  }, [])

  if (!images || images.length === 0) {
    return (
      <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-8 aspect-video flex items-center justify-center">
        <div className="text-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-12 w-12 mx-auto mb-4 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p className="text-gray-500 dark:text-gray-400">No images available</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
      {/* Main image */}
      <div className="relative aspect-video w-full overflow-hidden rounded-lg mb-4 group">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeImageIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="w-full h-full"
          >
            <OptimizedImage
              image={images[activeImageIndex]}
              alt={`${projectTitle} - Image ${activeImageIndex + 1}`}
              fill
              className="object-cover"
              priority={activeImageIndex === 0}
              sizes="(max-width: 768px) 100vw, 1200px"
              quality={90}
              onLoad={handleImageLoad}
            />
          </motion.div>
        </AnimatePresence>

        {/* Navigation arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Previous image"
              disabled={isLoading}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            <button
              onClick={nextImage}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Next image"
              disabled={isLoading}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </>
        )}

        {/* Image counter */}
        {images.length > 1 && (
          <div className="absolute bottom-2 right-2 bg-black/50 text-white px-2 py-1 rounded text-sm">
            {activeImageIndex + 1} / {images.length}
          </div>
        )}
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="flex space-x-2 overflow-x-auto pb-2">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => setActiveImageIndex(index)}
              className={`relative w-20 h-20 rounded-md overflow-hidden flex-shrink-0 transition-all ${
                activeImageIndex === index
                  ? 'ring-2 ring-primary shadow-lg'
                  : 'opacity-70 hover:opacity-100 hover:ring-1 hover:ring-gray-300'
              }`}
              aria-label={`View image ${index + 1}`}
            >
              <OptimizedImage
                image={image}
                alt={`${projectTitle} - Thumbnail ${index + 1}`}
                fill
                className="object-cover"
                quality={60}
                sizes="80px"
              />
              {activeImageIndex === index && <div className="absolute inset-0 bg-primary/10" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default ProjectGallery
